import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  Building2,
  DollarSign,
  FileText,
  TrendingUp,
  Info,
  Wallet,
} from "lucide-react";
import { formatMoney } from "../../utils/formatMoney";
import date from "date-and-time";

const AccountInfoBar = ({ account, className = "" }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pattern = date.compile("MM/DD/YYYY");

  if (!account) return null;

  // Helper function to split camelCase
  const splitCamelCase = (str) => {
    return str.replace(/([a-z])([A-Z])/g, "$1 $2");
  };

  // Animation variants
  const expandAnimation = {
    hidden: {
      height: 0,
      opacity: 0,
    },
    visible: {
      height: "auto",
      opacity: 1,
      transition: {
        height: {
          duration: 0.3,
          ease: "easeInOut",
        },
        opacity: {
          duration: 0.2,
          delay: 0.1,
        },
      },
    },
    exit: {
      height: 0,
      opacity: 0,
      transition: {
        height: {
          duration: 0.2,
          ease: "easeInOut",
        },
        opacity: {
          duration: 0.1,
        },
      },
    },
  };

  const itemAnimation = {
    hidden: { x: -20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl border border-gray-700 overflow-hidden ${className}`}
    >
      {/* Main Bar - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 sm:p-5 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
          {/* Icon - Hidden on mobile */}
          <div className="hidden sm:flex w-14 h-14 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl items-center justify-center flex-shrink-0">
            <Wallet className="w-7 h-7 text-blue-400" />
          </div>

          {/* Account Info */}
          <div className="flex-1 min-w-0 text-left">
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 mb-1">
              <h3 className="text-white font-semibold text-sm sm:text-base truncate">
                {account.chargeoff_creditor?.name ||
                  account.creditor?.name ||
                  account.name ||
                  "Account"}
              </h3>
              <span className="text-gray-400 text-xs sm:text-sm">
                #
                {account.chargeoff_creditor?.ref_id || account.creditor?.ref_id}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-400" />
                <span className="text-2xl sm:text-3xl font-bold text-white">
                  {formatMoney(account.balance_current || account.balance)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Expand/Collapse Button */}
        <div className="ml-4 flex items-center gap-2">
          <span className="hidden sm:block text-gray-400 text-sm">
            {isExpanded ? "Hide" : "View"} Details
          </span>
          <div className="w-10 h-10 bg-gray-700/50 rounded-lg flex items-center justify-center group-hover:bg-gray-700 transition-colors">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-300" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-300" />
            )}
          </div>
        </div>
      </button>

      {/* Expanded Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            variants={expandAnimation}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="border-t border-gray-700 bg-gray-900/50"
          >
            <div className="p-4 sm:p-6 space-y-4">
              {/* Balance Breakdown */}
              {account.balance_buckets &&
                account.balance_buckets.length > 0 && (
                  <motion.div
                    variants={itemAnimation}
                    initial="hidden"
                    animate="visible"
                    className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-5 h-5 text-blue-400" />
                      <h4 className="text-white font-semibold">
                        Balance Breakdown
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {account.balance_buckets.map((bucket, index) => {
                        const value = bucket.balance_current;
                        const displayValue =
                          value == null || isNaN(value)
                            ? "$0.00"
                            : `$${formatMoney(value)}`;

                        return (
                          <div
                            key={index}
                            className="flex justify-between items-center py-1"
                          >
                            <span className="text-gray-400 text-sm">
                              {splitCamelCase(bucket.name)}
                            </span>
                            <span className="text-white font-medium">
                              {displayValue}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

              {/* Agency Information */}
              {account.agency && (
                <motion.div
                  variants={itemAnimation}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.1 }}
                  className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-5 h-5 text-purple-400" />
                    <h4 className="text-white font-semibold">Agency</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-400 text-sm">Name</span>
                      <span className="text-white text-sm text-right">
                        {account.agency.name}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Account #</span>
                      <span className="text-white font-mono text-sm">
                        {account.agency.ref_id}
                      </span>
                    </div>
                    {/* {account.agency.last_payment_amount !== undefined &&
                      account.agency.last_payment_amount !== 0 && (
                        <div className="flex justify-between items-start pt-2 border-t border-gray-700/50">
                          <span className="text-gray-400 text-sm">
                            Last Payment
                          </span>
                          <div className="text-right">
                            <span className="text-white text-sm block">
                              ${formatMoney(account.agency.last_payment_amount)}
                            </span>
                            {account.agency.last_payment_date && (
                              <span className="text-gray-500 text-xs">
                                {date
                                  .format(
                                    new Date(account.agency.last_payment_date),
                                    pattern
                                  )
                                  .toLocaleLowerCase()}
                              </span>
                            )}
                          </div>
                        </div>
                      )} */}
                  </div>
                </motion.div>
              )}

              {/* Creditor Information */}
              {account.creditor && (
                <motion.div
                  variants={itemAnimation}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.2 }}
                  className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-green-400" />
                    <h4 className="text-white font-semibold">Creditor</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-400 text-sm">Name</span>
                      <span className="text-white text-sm text-right">
                        {account.creditor.name}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Account #</span>
                      <span className="text-white font-mono text-sm">
                        {account.creditor.ref_id}
                      </span>
                    </div>
                    {/* {account.creditor.last_payment_amount !== undefined &&
                      account.creditor.last_payment_amount !== 0 && (
                        <div className="flex justify-between items-start pt-2 border-t border-gray-700/50">
                          <span className="text-gray-400 text-sm">
                            Last Payment
                          </span>
                          <div className="text-right">
                            <span className="text-white text-sm block">
                              $
                              {formatMoney(
                                account.creditor.last_payment_amount
                              )}
                            </span>
                            {account.creditor.last_payment_date && (
                              <span className="text-gray-500 text-xs">
                                {date
                                  .format(
                                    new Date(
                                      account.creditor.last_payment_date
                                    ),
                                    pattern
                                  )
                                  .toLocaleLowerCase()}
                              </span>
                            )}
                          </div>
                        </div>
                      )} */}
                  </div>
                </motion.div>
              )}

              {/* Charge-off Creditor Information */}
              {account.chargeoff_creditor && (
                <motion.div
                  variants={itemAnimation}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.3 }}
                  className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-5 h-5 text-orange-400" />
                    <h4 className="text-white font-semibold">
                      Creditor at Charge Off
                    </h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-400 text-sm">Name</span>
                      <span className="text-white text-sm text-right">
                        {account.chargeoff_creditor.name}
                      </span>
                    </div>
                    {account.chargeoff_creditor.ref_id && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Account #</span>
                        <span className="text-white font-mono text-sm">
                          {account.chargeoff_creditor.ref_id}
                        </span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AccountInfoBar;
